<!--
Copyright (C) 2024 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import axios from 'axios'
import { type I18NPingHost, type ModeHostSite } from 'cmk-shared-typing/typescript/mode_host'
import { type Ref, computed, onMounted, ref } from 'vue'

import usei18n from '@/lib/i18n'
import usePersistentRef from '@/lib/usePersistentRef'

import CmkAlertBox, { type Variants } from '@/components/CmkAlertBox.vue'

// eslint-disable-next-line @typescript-eslint/naming-convention
declare let global_csrf_token: string

const props = defineProps<{
  i18n: I18NPingHost
  formElement: HTMLFormElement
  ipAddressFamilySelectElement: HTMLSelectElement
  ipAddressFamilyDefaultElement: HTMLDivElement
  ipAddressFamilyInputElement: HTMLInputElement
  hostnameInputElement: HTMLInputElement
  ipv4InputElement: HTMLInputElement
  ipv4InputButtonElement: HTMLInputElement
  ipv6InputElement: HTMLInputElement
  ipv6InputButtonElement: HTMLInputElement
  relayInputButtonElement: HTMLInputElement | null
  relaySelectElement: HTMLSelectElement | null
  relayDefaultElement: HTMLDivElement | null
  siteSelectElement: HTMLSelectElement
  siteInputElement: HTMLInputElement | null
  siteDefaultElement: HTMLDivElement | null
  sites: Array<ModeHostSite>
  defaultRelayIdHash: string
}>()

interface PingHostResponseError {
  result_code: 1
  result: string
}

interface PingHostResponseSuccess {
  result_code: 0
  result: {
    status_code: number
    message: string
  }
}

type PingHostResponse = PingHostResponseError | PingHostResponseSuccess

enum PingCmd {
  Ping = 'ping',
  Ping6 = 'ping6'
}
interface PingStatusInformation {
  queriesMade: number
  timerStarted: number | null
}

interface PingStatus {
  [PingCmd.Ping]: PingStatusInformation
  [PingCmd.Ping6]: PingStatusInformation
}

interface Result {
  status: DNSStatus
  element: HTMLInputElement
}

const { _t } = usei18n()
const prevStatusElements: Ref<Record<string, Result>> = ref({})
const statusElements: Ref<Record<string, Result>> = ref({})
const isNoIP = ref(
  (props.ipAddressFamilyInputElement.checked &&
    props.ipAddressFamilySelectElement.value === 'no-ip') ||
    props.ipAddressFamilyDefaultElement.textContent?.includes('No IP')
)
const isRelay = ref(checkRelay())
const controller = ref(new AbortController())
const ajaxRequestInProgress = ref<{ [key: string]: boolean }>({
  ping: false,
  ping6: false
})
const pingStatus: Ref<PingStatus> = usePersistentRef<PingStatus>(
  'ping_status',
  {
    ping: {
      queriesMade: 0,
      timerStarted: null
    },
    ping6: {
      queriesMade: 0,
      timerStarted: null
    }
  },
  (v) => v as PingStatus,
  'local'
)
const typingTimer: Ref<{ [key: string]: ReturnType<typeof setTimeout> | null }> = ref({
  ping: null,
  ping6: null
})
const doneTypingInterval = 2000
const maxQueries = 5
const waitTimeBetweenQueries = 60 * 1000

function checkRelay(): boolean {
  if (!props.relayInputButtonElement || !props.relaySelectElement || !props.relayDefaultElement) {
    return false
  }
  return (
    (props.relayInputButtonElement.checked === true &&
      props.relaySelectElement.value !== props.defaultRelayIdHash) ||
    (props.relayInputButtonElement.checked === false &&
      !props.relayDefaultElement.textContent?.includes('No relay'))
  )
}
const showPingHost = computed(() => {
  return !isNoIP.value && !isRelay.value
})

onMounted(() => {
  if (props.ipv4InputElement.value && showPingHost.value) {
    callPingHostOnElement(props.ipv4InputElement, PingCmd.Ping, true)
  }
  if (props.ipv6InputElement.value && showPingHost.value) {
    callPingHostOnElement(props.ipv6InputElement, PingCmd.Ping6, true)
  }
  props.formElement.addEventListener('change', (e: Event) => {
    switch (e.target) {
      case props.formElement:
        switch (props.ipAddressFamilySelectElement.value) {
          case 'ip-v4-only':
          case 'ip-v6-only':
          case 'ip-v4v6':
            isNoIP.value = false
            break
          case 'no-ip':
            isNoIP.value = true
            break
        }
        isRelay.value = checkRelay()
        break
      case props.ipAddressFamilyInputElement:
        isNoIP.value =
          (props.ipAddressFamilyInputElement.checked &&
            props.ipAddressFamilySelectElement.value === 'no-ip') ||
          (!props.ipAddressFamilyInputElement.checked &&
            props.ipAddressFamilyDefaultElement.textContent?.includes('No IP'))
        break
      case props.relayInputButtonElement:
        isRelay.value = checkRelay()
        break
    }
    if (!showPingHost.value) {
      prevStatusElements.value = statusElements.value
      statusElements.value = {}
    } else if (
      Object.keys(prevStatusElements.value).length > 0 &&
      Object.keys(statusElements.value).length === 0
    ) {
      statusElements.value = prevStatusElements.value
    }
  })
  props.hostnameInputElement.addEventListener('input', () => {
    if (!showPingHost.value) {
      statusElements.value = {}
      return
    }
    if (props.ipv4InputButtonElement.checked || props.ipv6InputButtonElement.checked) {
      return
    }
    callPingHostOnElement(props.hostnameInputElement, PingCmd.Ping, false)
  })
  props.ipv4InputElement.addEventListener('input', () => {
    if (!showPingHost.value) {
      statusElements.value = {}
      return
    }
    callPingHostOnElement(props.ipv4InputElement, PingCmd.Ping, true)
  })
  props.ipv6InputElement.addEventListener('input', () => {
    if (!showPingHost.value) {
      statusElements.value = {}
      return
    }
    callPingHostOnElement(props.ipv6InputElement, PingCmd.Ping6, true)
  })
  props.ipv4InputButtonElement.addEventListener('change', () => {
    if (props.ipv4InputButtonElement.checked) {
      if (showPingHost.value) {
        statusElements.value = {}
      }
      if (props.ipv4InputElement.value) {
        callPingHostOnElement(props.ipv4InputElement, PingCmd.Ping, true)
        return
      }
    }
  })
  props.ipv6InputButtonElement.addEventListener('change', () => {
    if (props.ipv6InputButtonElement.checked) {
      if (showPingHost.value) {
        statusElements.value = {}
      }
      if (props.ipv6InputElement.value) {
        callPingHostOnElement(props.ipv6InputElement, PingCmd.Ping6, true)
        return
      }
    }
  })
})

function callPingHostOnElement(
  element: HTMLInputElement,
  cmd: PingCmd,
  isIpAddress: boolean
): void {
  if (typingTimer.value[cmd]) {
    clearTimeout(typingTimer.value[cmd])
  }
  const elementName = element.name
  if (!elementName) {
    return
  }
  if (!element.value || element.value.trim() === '') {
    delete statusElements.value[elementName]
    return
  }
  if (props.hostnameInputElement.value) {
    delete statusElements.value[props.hostnameInputElement.name]
  }

  typingTimer.value[cmd] = setTimeout(() => {
    statusElements.value[elementName] = {
      status: {
        tooltip: props.i18n.loading,
        status: 'loading'
      },
      element: element
    }
    callAJAX(element, cmd, isIpAddress)
      .then((result) => {
        if (result && statusElements.value[elementName]) {
          statusElements.value[elementName].status = result
        }
      })
      .catch(() => {})
  }, doneTypingInterval)
}

async function callAJAX(
  inputElement: HTMLInputElement,
  cmd: PingCmd = PingCmd.Ping,
  isIpAddress: boolean = false
): Promise<DNSStatus | null> {
  if (ajaxRequestInProgress.value[cmd]) {
    controller.value.abort('New request triggered, aborting previous one')
  }
  while (controller.value.signal.aborted) {
    // Wait for the previous request to finish
    await new Promise((resolve) => setTimeout(resolve, 10))
  }

  const remainingTime =
    waitTimeBetweenQueries - (Date.now() - (pingStatus.value[cmd].timerStarted || 0))
  if (remainingTime < 0 && pingStatus.value[cmd].timerStarted) {
    pingStatus.value = {
      ...pingStatus.value,
      [cmd]: { ...pingStatus.value[cmd], queriesMade: 0, timerStarted: null }
    }
  }

  if (!pingStatus.value[cmd].timerStarted) {
    pingStatus.value = {
      ...pingStatus.value,
      [cmd]: { ...pingStatus.value[cmd], timerStarted: Date.now() }
    }
  }

  if (
    pingStatus.value[cmd].queriesMade > maxQueries &&
    pingStatus.value[cmd].timerStarted &&
    remainingTime > 0
  ) {
    setTimeout(() => {
      callPingHostOnElement(inputElement, cmd, isIpAddress)
    }, remainingTime)

    return {
      tooltip: _t(
        `Maximum number of ping attempts reached.
        After ${(remainingTime / 1000).toFixed(0)} seconds the next ping attempt will be made automatically.`
      ),
      status: 'warning'
    }
  }

  pingStatus.value = {
    ...pingStatus.value,
    [cmd]: { ...pingStatus.value[cmd], queriesMade: pingStatus.value[cmd].queriesMade + 1 }
  }

  const siteId =
    props.siteInputElement === null || props.siteInputElement.checked
      ? props.sites.find((site) => site.id_hash === props.siteSelectElement.value)?.site_id
      : (props.siteDefaultElement?.textContent?.split(' - ')[0] ?? undefined)
  const currentInput = inputElement.value ? encodeURIComponent(inputElement.value) : undefined

  if (!currentInput) {
    return null
  }

  ajaxRequestInProgress.value[cmd] = true
  return await axios
    .post('ajax_ping_host.py', null, {
      signal: controller.value.signal,
      params: {
        site_id: siteId ? encodeURIComponent(siteId) : undefined,
        ip_or_dns_name: currentInput,
        cmd: cmd,
        _csrf_token: global_csrf_token
      }
    })
    .then((response) => {
      if (response.data) {
        return handlePingHostResult(response.data, isIpAddress)
      }
      return null
    })
    .catch(() => {
      controller.value = new AbortController()
      return null
    })
    .finally(() => {
      ajaxRequestInProgress.value[cmd] = false
    })
}

interface DNSStatus {
  tooltip: string
  status: Variants
}

function handlePingHostResult(response: PingHostResponse, isIpAddress: boolean): DNSStatus {
  if (response!.result_code === undefined) {
    return {
      tooltip: _t('Unexpected response from server please check your crash reports and logs.'),
      status: 'error'
    }
  }
  switch (response.result_code) {
    case 0:
      switch (response.result.status_code) {
        case 0:
          return {
            tooltip: isIpAddress
              ? props.i18n.success_ip_pingable
              : props.i18n.success_host_dns_resolvable,
            status: 'success'
          }
        case 1:
          return {
            tooltip: isIpAddress
              ? props.i18n.error_ip_not_pingable
              : _t(`Cannot reach host. Host is resolvable via DNS but not pingable.
              Enter a reachable host name or IP address or set 'No IP' in the 'Network address' section.`),
            status: 'warning'
          }
        case 2:
          return {
            tooltip: isIpAddress
              ? props.i18n.error_ip_not_pingable
              : props.i18n.error_host_not_dns_resolvable,
            status: 'warning'
          }
        case 99:
          return {
            tooltip: response.result.message,
            status: 'error'
          }
        default:
          return {
            tooltip: _t('Unknown status code received from server.'),
            status: 'error'
          }
      }
    case 1:
      return {
        tooltip: response.result,
        status: 'error'
      }
  }
}
</script>

<template>
  <Teleport
    v-for="[elementName, { status, element }] in Object.entries(statusElements)"
    :key="elementName"
    :to="element.parentNode"
    defer
  >
    <span class="mh-ping-host__status-box">
      <CmkAlertBox
        :title="status.tooltip"
        :variant="status.status"
        size="small"
        class="mh-ping-host__status-box-alert"
      >
        {{ status.tooltip }}
      </CmkAlertBox>
    </span>
  </Teleport>
</template>

<style scoped>
.mh-ping-host__status-box {
  display: inline-block;
  position: relative;
  top: 4px;

  .mh-ping-host__status-box-alert {
    margin: 0;
  }
}
</style>
